/**
 * ErrorBoundary — capture les erreurs React qui crashent silencieusement
 * un composant et le re-rendent en page blanche.
 *
 * Sans cette boundary, une exception jetée pendant le rendu d'un enfant
 * fait crasher tout l'arbre React sans message visible. C'est la cause
 * typique des "pages blanches" après un clic.
 *
 * Usage :
 *   <ErrorBoundary fallback={(err, reset) => <div>Oups : {err.message}</div>}>
 *     <ComposantQuiPourraitCrasher />
 *   </ErrorBoundary>
 */
import React from 'react';
import i18n from '../../i18n';

// Détecte un échec de chargement de chunk JS (lazy import) ou de bundle
// principal. Ces erreurs surviennent typiquement APRÈS un nouveau déploiement
// quand un onglet déjà ouvert tente de charger un bundle qui n'existe plus
// (cf. vercel.json + commentaire SPA fallback).
const isChunkLoadError = (error) => {
  if (!error) return false;
  const msg = String(error?.message || error || '');
  const name = String(error?.name || '');
  return (
    name === 'ChunkLoadError'
    || /Loading chunk \S+ failed/i.test(msg)
    || /Loading CSS chunk \S+ failed/i.test(msg)
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg)
    || /Refused to execute .* as script/i.test(msg)
    || /Unexpected token '<'/i.test(msg) // typique d'un JS reçu en HTML
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    // Log dans la console pour que l'utilisateur puisse voir le détail
    // dans devtools même si l'UI affiche seulement un message synthétique.
    console.error('[ErrorBoundary] crash capturé :', error);
    console.error('[ErrorBoundary] component stack :', errorInfo?.componentStack);
    this.setState({ errorInfo });

    // Auto-reload sur ChunkLoadError : déploiement plus récent disponible.
    // On utilise sessionStorage pour éviter une boucle infinie de reload
    // si le problème persiste (ex: bug réel dans le nouveau build).
    if (isChunkLoadError(error)) {
      const RELOAD_KEY = 'pfm_chunk_reload_attempts';
      const MAX_ATTEMPTS = 2;
      const COOLDOWN_MS = 60_000;  // 1 min
      try {
        const raw = sessionStorage.getItem(RELOAD_KEY);
        const state = raw ? JSON.parse(raw) : { count: 0, ts: 0 };
        const now = Date.now();
        // Reset le compteur si plus d'une minute s'est écoulée
        if (now - state.ts > COOLDOWN_MS) {
          state.count = 0;
        }
        if (state.count < MAX_ATTEMPTS) {
          state.count += 1;
          state.ts = now;
          sessionStorage.setItem(RELOAD_KEY, JSON.stringify(state));
          // Petit délai pour laisser le toast/UI s'afficher si présent
          setTimeout(() => this.rechargerSiServeurJoignable(), 800);
        }
      } catch (e) {
        // sessionStorage indisponible (mode privé Safari ?) → on reload quand même
        setTimeout(() => this.rechargerSiServeurJoignable(), 800);
      }
    }
  }

  // Sans réseau, recharger la page la rendrait vide (rien pour la servir) et le pompier
  // perdrait l'accès à ses visites préparées : on recharge seulement si le serveur répond.
  rechargerSiServeurJoignable = () => {
    fetch(`/manifest.json?verif=${Date.now()}`, { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        console.log('[ErrorBoundary] ChunkLoadError détecté — auto-reload de la page');
        window.location.reload();
      })
      .catch(() => this.setState({ horsLigne: true }));
  };

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, isChunkError: false, horsLigne: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback, label } = this.props;
      const { error, errorInfo, isChunkError } = this.state;

      // Cas spécial : erreur de chargement de chunk JS (déploiement obsolète).
      // L'auto-reload est en cours (cf. componentDidCatch) — on affiche un
      // message d'attente plutôt qu'un error stack inutile.
      if (isChunkError && this.state.horsLigne) {
        return (
          <div style={{
            padding: '2rem',
            margin: '1rem auto',
            maxWidth: 480,
            textAlign: 'center',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: '8px',
            color: '#92400e',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📴</div>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              {i18n.t('horsLigne.pageIndisponibleTitre')}
            </h3>
            <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              {i18n.t('horsLigne.pageIndisponibleTexte')}
            </p>
            <button
              onClick={this.reset}
              style={{
                padding: '0.5rem 1rem',
                background: '#d97706',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {i18n.t('horsLigne.reessayer')}
            </button>
          </div>
        );
      }

      if (isChunkError) {
        return (
          <div style={{
            padding: '2rem',
            margin: '1rem auto',
            maxWidth: 480,
            textAlign: 'center',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            color: '#1e40af',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔄</div>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              {i18n.t('errorBoundary.miseAJourDeLApplication')}
            </h3>
            <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              {i18n.t('errorBoundary.uneNouvelleVersionEstDisponibleRechargem')}
            </p>
            <button
              onClick={this.handleReload}
              style={{
                padding: '0.5rem 1rem',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {i18n.t('errorBoundary.rechargerMaintenant')}
            </button>
          </div>
        );
      }

      if (fallback) {
        return fallback(error, this.reset, errorInfo);
      }

      // Fallback par défaut : message visible avec détails techniques dépliables
      return (
        <div style={{
          padding: '1.5rem',
          margin: '1rem',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            ⚠️ {label || i18n.t('errorBoundary.uneErreurEstSurvenue')}
          </h3>
          <p style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
            {error?.message || String(error) || i18n.t('errorBoundary.erreurInconnue')}
          </p>
          <details style={{ fontSize: '0.75rem', color: '#7c2d12' }}>
            <summary style={{ cursor: 'pointer', marginBottom: '0.4rem' }}>
              {i18n.t('errorBoundary.detailsTechniquesPourLeDeveloppeur')}
            </summary>
            <pre style={{
              background: 'white',
              padding: '0.5rem',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '300px',
              fontSize: '0.7rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {error?.stack || ''}
              {errorInfo?.componentStack || ''}
            </pre>
          </details>
          <button
            onClick={this.reset}
            style={{
              marginTop: '0.75rem',
              padding: '0.4rem 0.8rem',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            {i18n.t('errorBoundary.reessayer')}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
